import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  statusBadgeHeaderClass,
  type SupportMessage,
  type SupportTicket,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from './types';

interface UserOption {
  id: string;
  name: string;
  email: string;
}

type ReplyMode = 'reply' | 'internal';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function userInitials(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function formatStudentLabel(name: string, email: string): string {
  const trimmedName = name.trim();
  if (!trimmedName || trimmedName === email) return email;
  return `${trimmedName} (${email})`;
}

function messageVariant(message: SupportMessage, studentId: string): 'student' | 'staff' | 'internal' {
  if (message.isInternal) return 'internal';
  if (message.author.id === studentId) return 'student';
  return 'staff';
}

export function SoporteAdminTicketPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canAdminister = hasPermission(PERMISSIONS.SOPORTE_ADMINISTRAR);
  const [reply, setReply] = useState('');
  const [replyMode, setReplyMode] = useState<ReplyMode>('reply');
  const [status, setStatus] = useState<SupportTicketStatus | ''>('');
  const [priority, setPriority] = useState<SupportTicketPriority | ''>('');
  const [assigneeId, setAssigneeId] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['support', 'admin', 'tickets', id],
    queryFn: () => api<SupportTicket>(`/support/tickets/${id}`),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!ticket) return;
    setStatus(ticket.status);
    setPriority(ticket.priority);
    setAssigneeId(ticket.assignee?.id ?? '');
  }, [ticket]);

  const messageCount = ticket?.messages?.length ?? 0;
  const lastMessageId = ticket?.messages?.at(-1)?.id;

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread || messageCount === 0) return;

    const scrollToBottom = () => {
      thread.scrollTop = thread.scrollHeight;
    };

    scrollToBottom();
    const frame = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(frame);
  }, [id, messageCount, lastMessageId]);

  const { data: assigneesData } = useQuery({
    queryKey: ['support', 'admin', 'assignees'],
    queryFn: () => api<{ data: UserOption[] }>('/support/tickets/assignees'),
    enabled: canAdminister,
  });

  const replyMutation = useMutation({
    mutationFn: (payload: { body: string; isInternal?: boolean }) =>
      api<SupportTicket>(`/support/tickets/${id}/reply`, { method: 'POST', body: payload }),
    onSuccess: async () => {
      setReply('');
      setReplyMode('reply');
      await queryClient.invalidateQueries({ queryKey: ['support', 'admin', 'tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['support', 'admin', 'tickets', id] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: {
      status?: SupportTicketStatus;
      priority?: SupportTicketPriority;
      assigneeId?: string | null;
    }) => api<SupportTicket>(`/support/tickets/${id}`, { method: 'PATCH', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['support', 'admin', 'tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['support', 'admin', 'tickets', id] });
    },
  });

  const assignees = useMemo(() => {
    const list = assigneesData?.data ?? [];
    const current = ticket?.assignee;
    if (current && !list.some((user) => user.id === current.id)) {
      return [current, ...list];
    }
    return list;
  }, [assigneesData, ticket?.assignee]);

  function handleReply(e: FormEvent) {
    e.preventDefault();
    if (!canAdminister || !reply.trim() || !ticket) return;
    replyMutation.mutate({
      body: reply.trim(),
      isInternal: canAdminister && replyMode === 'internal',
    });
  }

  function handleUpdate(e: FormEvent) {
    e.preventDefault();
    if (!canAdminister || !ticket) return;
    updateMutation.mutate({
      status: status || ticket.status,
      priority: priority || ticket.priority,
      assigneeId: assigneeId || null,
    });
  }

  if (isLoading || !ticket) {
    return (
      <div className="lms-support-admin lms-site-admin-hub-page">
        <div className="support-admin-loading">
          <Loader label="Cargando ticket…" />
        </div>
      </div>
    );
  }

  const messages = ticket.messages ?? [];
  const canReply = ticket.status !== 'cerrado';
  const studentLabel = formatStudentLabel(ticket.user.name, ticket.user.email);

  return (
    <div className="lms-support-admin lms-site-admin-hub-page">
      <header className="mb-6 flex items-start gap-4 font-sans">
        <HubBackLink to="/soporte/admin" label="Volver a la bandeja" />
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            Ticket #{ticket.number}
          </h1>
          <span className={statusBadgeHeaderClass(ticket.status)}>{ticket.statusLabel}</span>
        </div>
      </header>

      <div className="support-admin-ticket-info">
        <div className="support-admin-ticket-info-grid">
          <div>
            <span className="support-admin-ticket-info-label">Curso</span>
            <p className="support-admin-ticket-info-value">
              {ticket.course?.name ?? '—'}
            </p>
          </div>
          <div>
            <span className="support-admin-ticket-info-label">Alumno</span>
            <p className="support-admin-ticket-info-value support-admin-ticket-info-student">
              <span className="material-symbols-outlined text-[18px] text-[var(--support-outline)]" aria-hidden="true">
                person
              </span>
              <Link to={`/usuarios/${ticket.user.id}`} className="text-inherit hover:underline">
                {studentLabel}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="support-admin-ticket-layout">
        <div className="support-admin-ticket-main">
          <div ref={threadRef} className="support-admin-ticket-thread">
            {messages.map((message) => {
              const variant = messageVariant(message, ticket.user.id);
              const isStaff = variant === 'staff';
              const authorLabel =
                variant === 'internal'
                  ? `${message.author.name} (nota interna)`
                  : isStaff
                    ? message.author.name || 'Administrador'
                    : formatStudentLabel(message.author.name, message.author.email);

              return (
                <article
                  key={message.id}
                  className={`support-admin-ticket-message support-admin-ticket-message--${variant}`}
                >
                  <div className="support-admin-ticket-message-avatar" aria-hidden="true">
                    {isStaff ? (
                      <span className="material-symbols-outlined">support_agent</span>
                    ) : (
                      userInitials(message.author.name, message.author.email)
                    )}
                  </div>
                  <div className="support-admin-ticket-message-body">
                    <div className="support-admin-ticket-message-meta">
                      <span className="support-admin-ticket-message-author">{authorLabel}</span>
                      <time className="support-admin-ticket-message-time" dateTime={message.createdAt}>
                        {formatDate(message.createdAt)}
                      </time>
                    </div>
                    <p className="support-admin-ticket-message-text">{message.body}</p>
                  </div>
                </article>
              );
            })}
          </div>

          {canAdminister && canReply && (
            <form onSubmit={handleReply} className="support-admin-ticket-reply">
              <div className="support-admin-ticket-reply-tabs" role="tablist" aria-label="Tipo de respuesta">
                <button
                  type="button"
                  role="tab"
                  aria-selected={replyMode === 'reply'}
                  className={`support-admin-ticket-reply-tab ${replyMode === 'reply' ? 'support-admin-ticket-reply-tab--active' : ''}`}
                  onClick={() => setReplyMode('reply')}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    reply
                  </span>
                  Responder
                </button>
                {canAdminister && (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={replyMode === 'internal'}
                    className={`support-admin-ticket-reply-tab ${replyMode === 'internal' ? 'support-admin-ticket-reply-tab--active' : ''}`}
                    onClick={() => setReplyMode('internal')}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      lock
                    </span>
                    Nota interna (no visible para el alumno)
                  </button>
                )}
              </div>
              <div className="support-admin-ticket-reply-body">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  required
                  placeholder="Escribe tu respuesta aquí..."
                  className="support-admin-ticket-textarea"
                />
                <div className="support-admin-ticket-reply-actions">
                  <div className="support-admin-ticket-format-actions" aria-hidden="true">
                    <button type="button" className="support-admin-ticket-format-btn" tabIndex={-1}>
                      <span className="material-symbols-outlined">attach_file</span>
                    </button>
                    <button type="button" className="support-admin-ticket-format-btn" tabIndex={-1}>
                      <span className="material-symbols-outlined">format_bold</span>
                    </button>
                    <button type="button" className="support-admin-ticket-format-btn" tabIndex={-1}>
                      <span className="material-symbols-outlined">format_italic</span>
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={replyMutation.isPending}
                    className="support-admin-ticket-send-btn"
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      send
                    </span>
                    Enviar respuesta
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {canAdminister && (
          <aside className="support-admin-ticket-sidebar">
            <form onSubmit={handleUpdate} className="support-admin-ticket-config">
              <h2 className="support-admin-ticket-config-title">
                <span className="material-symbols-outlined" aria-hidden="true">
                  settings
                </span>
                Configuración del Ticket
              </h2>

              <div className="support-admin-ticket-config-fields">
                <div className="support-admin-field">
                  <label htmlFor="ticket-status">Estado</label>
                  <div className="support-admin-select-wrap">
                    <select
                      id="ticket-status"
                      value={status || ticket.status}
                      onChange={(e) => setStatus(e.target.value as SupportTicketStatus)}
                      className="support-admin-select"
                    >
                      {(Object.keys(STATUS_LABELS) as SupportTicketStatus[]).map((key) => (
                        <option key={key} value={key}>
                          {STATUS_LABELS[key]}
                        </option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      expand_more
                    </span>
                  </div>
                </div>

                <div className="support-admin-field">
                  <label htmlFor="ticket-priority">Prioridad</label>
                  <div className="support-admin-select-wrap">
                    <select
                      id="ticket-priority"
                      value={priority || ticket.priority}
                      onChange={(e) => setPriority(e.target.value as SupportTicketPriority)}
                      className="support-admin-select"
                    >
                      {(Object.keys(PRIORITY_LABELS) as SupportTicketPriority[]).map((key) => (
                        <option key={key} value={key}>
                          {PRIORITY_LABELS[key]}
                        </option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      expand_more
                    </span>
                  </div>
                </div>

                <div className="support-admin-field">
                  <label htmlFor="ticket-assignee">Asignado a</label>
                  <div className="support-admin-select-wrap">
                    <select
                      id="ticket-assignee"
                      value={assigneeId}
                      onChange={(e) => setAssigneeId(e.target.value)}
                      className="support-admin-select"
                    >
                      <option value="">Sin asignar</option>
                      {assignees.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name || user.email}
                        </option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      expand_more
                    </span>
                  </div>
                </div>

                <hr className="support-admin-ticket-config-divider" />

                <button type="submit" disabled={updateMutation.isPending} className="support-admin-ticket-update-btn">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    save
                  </span>
                  Actualizar
                </button>
              </div>
            </form>
          </aside>
        )}
      </div>
    </div>
  );
}
